import React, { useState, useEffect } from 'react';
import { Input, Button, List, Card, Drawer, Spin } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import StoreAdvancedFilterModal from './storeAdvancedFilterModal';
import { Settings2 } from 'lucide-react';
import { submitSets } from '../../../UiComponents/SubmitSets';
import { submitCollection } from '../../../../_services/submit.service';
import { useTranslation } from 'react-i18next';

const StoreSearchDropdown = ({ visible, onClose, onSelectStore, searchText, onSearchChange, clusterList, arrayList, departmentId, onRefreshClusters, dmode, onRefreshArrays, isRTL, preventAutoOpen, setPreventAutoOpen }) => {
  const [storeData, setStoreData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalToast, setGlobalToast] = useState(null);

  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  const {t} = useTranslation()

    const showGlobalToast = (type, message, duration = 3000) => {
        setGlobalToast({ type, message });
        setTimeout(() => setGlobalToast(null), duration);
    };

  const fetchFilteredStores = async (searchQuery = '') => {
    setLoading(true);
    try {
      const payload = {
        storeName: searchQuery || null,
        departmentId: departmentId
      };

      const response = await submitSets(
        submitCollection.filterStores,
        payload
      );

      if (response?.status && response?.extra) {
        const formattedStores = response.extra.map((store, index) => ({
          key: `store-${store.storeId}-${index}`,
          storeId: store.storeCode,
          storeName: store.storeName,
          regionName: store.regionName,
          tags: store.tagNames || [],
          linkedArray: store.linkedClusterName || '',
          width: store.width,
          noOfFields: store.noOfFields
        }));
        setStoreData(formattedStores);
      } else {
        setStoreData([]);
      }
    } catch (error) {
      setStoreData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchFilteredStores(searchText);
    }
  }, [visible]);

  useEffect(() => {
    if (visible && searchText !== undefined) {
      const timeoutId = setTimeout(() => {
        fetchFilteredStores(searchText);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [searchText, visible]);


  const filteredData = storeData;

  //  if (!visible) return null;

  const handleCloseAdvancedFilter = () => {
    setShowAdvancedFilter(false);
  };

  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        // right: 0,
        width: '87.5%',
        marginTop: '12px',
        zIndex: 1000,
        backgroundColor: dmode ? '#2f353e' : 'white',
        border: dmode ? '1px solid #555' : '1px solid #d9d9d9',
        borderRadius: '6px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        height: '60vh',
        overflow: 'hidden',
        display: (visible && !preventAutoOpen) ? 'block' : 'none',
      }}
    >
      <div style={{ padding: '8px', borderBottom: dmode ? '1px solid #555' : '1px solid #f0f0f0' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(60vh - 72px)' }}>
            <Spin size="large" />
          </div>
        ) : (
        <List
          size="small"
          dataSource={filteredData}
          style={{ height: 'calc(60vh - 72px)', overflow: 'auto' }}
          locale={{emptyText : t('NO_DATA')}}
          renderItem={(item) => (
            <List.Item
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                margin: '2px 0',
                borderBottom: dmode ? '1px solid #555' : '1px solid #BDBDBD'
              }}
              onClick={() => {
                onSelectStore?.(item);
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = dmode ? '#3a4149' : '#FFF6E5';
                // Change tag colors on hover
                const tags = e.currentTarget.querySelectorAll('span[style*="background"]');
                tags.forEach(tag => {
                  if (dmode) {
                    if (tag.style.background === 'rgb(74, 81, 88)') {
                      tag.style.background = '#5a6168';
                    }
                  } else {
                    if (tag.style.background === 'rgb(242, 241, 255)') {
                      tag.style.background = '#FFEAC3';
                    }
                  }
                });
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                // Restore tag colors
                const tags = e.currentTarget.querySelectorAll('span[style*="background"]');
                tags.forEach(tag => {
                  if (dmode) {
                    if (tag.style.background === 'rgb(90, 97, 104)') {
                      tag.style.background = '#4a5158';
                    }
                  } else {
                    if (tag.style.background === 'rgb(255, 234, 195)') {
                      tag.style.background = '#F2F1FF';
                    }
                  }
                });
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', textAlign: 'left', gap: '12px', fontFamily: 'Noto Sans, sans-serif' }}>
                <div style={{ flex: '1', minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: dmode ? '#ccc' : '#333' }}>
                    {item.storeName}
                  </div>
                  <div style={{ fontSize: '11px', color: dmode ? '#999' : '#333333' }}>{item.storeId}</div>
                </div>
                <div style={{ flex: '0 0 50%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                  <span style={{ fontSize: '12px', color: dmode ? '#999' : '#666', marginBottom: '4px', whiteSpace: 'nowrap' }}>{item.regionName}</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-start', maxWidth: '100%', overflow: 'hidden', maxHeight: '44px' }}>
                    {(() => {
                      if (!item.tags || item.tags.length === 0) return null;
                      
                      const maxTagsToShow = 4; // Approximate number of tags that fit in 2 lines
                      const tagsToShow = item.tags.slice(0, maxTagsToShow);
                      const hasMoreTags = item.tags.length > maxTagsToShow;
                      
                      return (
                        <>
                          {tagsToShow.map((tag, index) => (
                            <span key={index} style={{ 
                              padding: '0 6px', 
                              borderRadius: '4px', 
                              background: dmode ? '#4a5158' : '#F2F1FF', 
                              color: dmode ? '#fff' : '#000', 
                              fontSize: '11px', 
                              fontWeight: 500, 
                              border: 'none', 
                              display: 'inline-block',
                              whiteSpace: 'nowrap'
                            }}>{tag}</span>
                          ))}
                          {hasMoreTags && (
                            <span style={{ 
                              padding: '0 6px', 
                              borderRadius: '4px', 
                              background: dmode ? '#4a5158' : '#F2F1FF', 
                              color: dmode ? '#ccc' : '#666', 
                              fontSize: '11px', 
                              fontWeight: 500, 
                              border: 'none', 
                              display: 'inline-block',
                              whiteSpace: 'nowrap'
                            }}>...</span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </List.Item>
          )}
        />
        )}
      </div>

      <div style={{ padding: '12px', textAlign: 'center', borderTop: dmode ? '1px solid #555' : '1px solid #4F4F4F', height: '72px'}}>
        <Button
          size="small"
          style={{
            borderRadius: '4px',
            border: '1px solid #4F4F4F',
            padding: '2px 8px',
            color: dmode ? "#F2F1FF" :'#4F4F4F',
            fontWeight: 600,
            backgroundColor: dmode ? '#2f353e' : '#F2F1FF',
            height: 'auto',
          }}
          onMouseEnter={(e) => {
            e.stopPropagation();
            e.currentTarget.style.backgroundColor = dmode ? '#3a4149' :'#F2F1FF';
            e.currentTarget.style.borderColor = dmode ? '#2CC990' : '#5128A0';
            e.currentTarget.style.color = dmode ? '#2CC990' : '#5128A0';
            const icon = e.currentTarget.querySelector('svg');
            if (icon) {
              icon.style.stroke = dmode ? '#2CC990' : '#5128A0';
              icon.style.color = dmode ? '#2CC990' : '#5128A0';
            }
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            e.currentTarget.style.backgroundColor = dmode ? '#2f353e' : '#F2F1FF';
            e.currentTarget.style.borderColor = '#4F4F4F';
            e.currentTarget.style.color = dmode ? '#F2F1FF' : '#4F4F4F';
            const icon = e.currentTarget.querySelector('svg');
            if (icon) {
              icon.style.stroke = dmode ? "#F2F1FF" :'#4F4F4F';
              icon.style.color = dmode ? "#F2F1FF" :'#4F4F4F';
            }
          }}
          onMouseDown={(e) => {
            // Prevent input blur by preventing default mousedown behavior
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            // Prevent modal from closing when touching the button
            e.preventDefault();
            e.stopPropagation();
            // Handle touch start for mobile/touchpad
            e.currentTarget.style.backgroundColor = dmode ? '#3a4149' :'#F2F1FF';
            e.currentTarget.style.borderColor = dmode ? '#2CC990' : '#5128A0';
            e.currentTarget.style.color = dmode ? '#2CC990' : '#5128A0';
            const icon = e.currentTarget.querySelector('svg');
            if (icon) {
              icon.style.stroke = dmode ? '#2CC990' : '#5128A0';
              icon.style.color = dmode ? '#2CC990' : '#5128A0';
            }
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Trigger the action on touch end
            setShowAdvancedFilter(true);
            // Reset styles after touch
            setTimeout(() => {
              if (e.currentTarget) {
                e.currentTarget.style.backgroundColor = dmode ? '#2f353e' : '#F2F1FF';
                e.currentTarget.style.borderColor = '#4F4F4F';
                e.currentTarget.style.color = dmode ? '#F2F1FF' : '#4F4F4F';
                const icon = e.currentTarget.querySelector('svg');
                if (icon) {
                  icon.style.stroke = dmode ? "#F2F1FF" :'#4F4F4F';
                  icon.style.color = dmode ? "#F2F1FF" :'#4F4F4F';
                }
              }
            }, 150);
          }}
          onTouchCancel={(e) => {
            // Prevent modal from closing when touch is cancelled on button
            e.preventDefault();
            e.stopPropagation();
            // Reset styles if touch is cancelled
            e.currentTarget.style.backgroundColor = dmode ? '#2f353e' : '#F2F1FF';
            e.currentTarget.style.borderColor = '#4F4F4F';
            e.currentTarget.style.color = dmode ? '#F2F1FF' : '#4F4F4F';
            const icon = e.currentTarget.querySelector('svg');
            if (icon) {
              icon.style.stroke = dmode ? "#F2F1FF" :'#4F4F4F';
              icon.style.color = dmode ? "#F2F1FF" :'#4F4F4F';
            }
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowAdvancedFilter(true)
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {t('ADVANCED_FILTER')}
            <span style={{top: 2}}><Settings2 size={16} color={dmode ? "#F2F1FF" : "#4F4F4F"}/></span>
          </span>
        </Button>
      </div>

      {/* Store Advanced Filter Modal (Side Modal) */}
      {/* <Drawer
        onClose={handleCloseAdvancedFilter}
        open={showAdvancedFilter}
      > */}
        <StoreAdvancedFilterModal
          show={showAdvancedFilter}
          onClose={handleCloseAdvancedFilter}
          onApplyFilter={() => {}}
          isRTL={isRTL === 'rtl'}
          clusterList={clusterList}
          arrayList={arrayList}
          departmentId={departmentId}
          onRefreshClusters={onRefreshClusters}
          onRefreshArrays={onRefreshArrays}
          dmode={dmode}
          onShowToast={showGlobalToast}
        />
      {/* </Drawer> */}

            {globalToast && (
                <div style={{
                    position: 'fixed',
                    top: '13%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10001,
                    padding: '12px 16px',
                    borderRadius: '6px',
                    backgroundColor: globalToast.type === 'success' ? '#4CAF50' : globalToast.type === 'warning' ? '#FF9800' : '#EB5757',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                    fontSize: '14px',
                    fontWeight: '600',
                    minWidth: '300px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                        <span>{globalToast.message}</span>
                        <span
                            onClick={() => setGlobalToast(null)}
                            style={{
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px',
                                transition: 'opacity 0.2s',
                                opacity: 1,
                                marginLeft: '4px',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                        </span>
                    </div>
                </div>
            )}
    </div>
  );
};

export default StoreSearchDropdown;
